/**
 * Invitation Service
 * Handles user invitation management API calls
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface Invitation {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: 'Pending' | 'Accepted' | 'Expired' | 'Cancelled';
  invitedByName: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
}

export interface CreateInvitationRequest {
  email: string;
  firstName: string;
  lastName: string;
  role?: 'ClientUser' | 'ClientAdmin';
}

export interface InvitationValidation {
  isValid: boolean;
  email: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  expiresAt: string;
  message?: string;
}

export interface AcceptInvitationRequest {
  password: string;
  confirmPassword: string;
}

export interface AcceptInvitationResponse {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  message: string;
}

/**
 * List all invitations for the organization
 */
export const listInvitations = async (): Promise<Invitation[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.INVITATIONS.LIST);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Create a new invitation
 */
export const createInvitation = async (data: CreateInvitationRequest): Promise<Invitation> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.INVITATIONS.CREATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Resend an invitation
 */
export const resendInvitation = async (invitationId: string): Promise<Invitation> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.INVITATIONS.RESEND(invitationId), {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Cancel an invitation
 */
export const cancelInvitation = async (invitationId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.INVITATIONS.CANCEL(invitationId), {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }
};

/**
 * Validate an invitation token (public - no auth required)
 */
export const validateInvitationToken = async (token: string): Promise<InvitationValidation> => {
  const response = await fetch(API_ENDPOINTS.INVITATIONS.VALIDATE_TOKEN(token), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Accept an invitation (public - no auth required)
 */
export const acceptInvitation = async (
  token: string,
  data: AcceptInvitationRequest
): Promise<AcceptInvitationResponse> => {
  const response = await fetch(API_ENDPOINTS.INVITATIONS.ACCEPT_TOKEN(token), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};
